import stateManager from './data/state_manager.js';

// FeatureTour Class Definition
class FeatureTour {
  constructor(steps, scrollableElement, enabledByDefault) {
    this.steps = steps;
    this.currentStep = 0;

    // Bind methods to ensure correct 'this' context
    this.showStep = this.showStep.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.endTour = this.endTour.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.handleSkip = this.handleSkip.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this); // Bind the new methods
    this.scrollableElement = scrollableElement;
    this.scrollTimer = null;
    this.resizeTimer = null;
    this.enabled = enabledByDefault;

    // Keyboard detection properties
    this.initialViewportHeight = window.visualViewport
      ? window.visualViewport.height
      : window.innerHeight;
    this.keyboardVisible = false;
    this.keyboardThreshold = 100; // Adjust based on testing

    // Focus tracking
    this.focusCounter = 0;
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);

    // Animation frame flags
    this.rafId = null;

    // Visibility state
    this.isVisible = false; // Track whether the tour is currently visible
  }

  start() {
    const { tourSkipped } = stateManager.getState();

    if (!this.enabled || tourSkipped) {
      return;
    }

    // Bind event listeners for Next and Skip buttons
    const nextButton = document.getElementById('tourNextButton');
    const skipButton = document.getElementById('tourSkipButton');

    if (nextButton) {
      nextButton.addEventListener('click', this.handleNext);
    }

    if (skipButton) {
      skipButton.addEventListener('click', this.handleSkip);
    }

    // Start the first step after ensuring the DOM is ready and a short timeout
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            this.showStep(this.currentStep);
            this.show(); // Ensure the tour is visible when starting
          }, 100); // 100ms delay
        });
      });
    });

    // Add click listener to overlay to prevent interactions
    const overlay = document.getElementById('overlay');
    if (overlay) {
      overlay.addEventListener('click', this.handleOverlayClick);
    }

    // Scroll and Resize event handlers using requestAnimationFrame
    const scrollHandler = () => {
      if (this.keyboardVisible) return;
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        const step = this.steps[this.currentStep];
        const element = this.findElement(step.elementId);
        if (!element) {
          console.warn('Element not found for step:', step);
          this.rafId = null;
          return;
        }
        const rect = this.showFourOverlays(element);
        this.positionTooltip(rect, this.currentStep.position || 'above');
        this.rafId = null;
      });
    };

    const resizeHandler = () => {
      const step = this.steps[this.currentStep];
      const element = this.findElement(step.elementId);
      if (!element) {
        console.warn('Element not found for step:', step);
        return;
      }

      // Detect keyboard visibility based on viewport height
      const currentViewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;
      const heightDifference =
        this.initialViewportHeight - currentViewportHeight;

      if (heightDifference > this.keyboardThreshold) {
        // Keyboard is likely visible
        if (!this.keyboardVisible) {
          this.keyboardVisible = true;
          console.log('Keyboard detected: Pausing overlay recalculations.');
        }
        return; // Skip recalculations while keyboard is visible
      } else {
        // Keyboard is likely hidden
        if (this.keyboardVisible) {
          this.keyboardVisible = false;
          console.log('Keyboard dismissed: Resuming overlay recalculations.');
          // Recalculate overlays after keyboard is dismissed
          this.showStep(this.currentStep);
        }
      }

      if (this.keyboardVisible) return;

      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        const rect = this.showFourOverlays(element);
        this.positionTooltip(rect, this.currentStep.position || 'above');
        this.rafId = null;
      });
    };

    this.scrollableElement.addEventListener('scroll', scrollHandler);
    this.resizeHandler = resizeHandler;
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', resizeHandler);
    } else {
      window.addEventListener('resize', resizeHandler);
    }

    this.scrollHandler = scrollHandler; // Store reference for removal

    // Handle input focus and blur to track keyboard visibility
    this.inputs = document.querySelectorAll('input, textarea, select');
    this.inputs.forEach((input) => {
      input.addEventListener('focus', this.handleInputFocus);
      input.addEventListener('blur', this.handleInputBlur);
    });
  }

  handleInputFocus() {
    this.focusCounter += 1;
    if (this.focusCounter === 1) {
      // First input focused, assume keyboard is visible
      this.keyboardVisible = true;
      console.log('Input focused: Keyboard likely visible.');
    }
  }

  handleInputBlur() {
    this.focusCounter = Math.max(this.focusCounter - 1, 0);
    if (this.focusCounter === 0) {
      // All inputs blurred, assume keyboard is hidden
      this.keyboardVisible = false;
      console.log('Input blurred: Keyboard likely hidden.');
      // Recalculate overlays after a short delay to allow viewport to adjust
      setTimeout(() => {
        this.showStep(this.currentStep);
      }, 300); // 300ms delay to allow for keyboard dismissal
    }
  }

  showStep(index) {
    const { tourSkipped } = stateManager.getState();

    if (!this.enabled || tourSkipped) {
      return;
    }

    if (index >= this.steps.length) {
      this.endTour();
      return;
    }

    const step = this.steps[index];
    const element = this.findElement(step.elementId);

    if (!element) {
      console.warn('No elements found for step:', step);
      this.nextStep(); // Proceed to next step if no element found
      return;
    }

    // Highlight area by placing overlays around it
    const rect = this.showFourOverlays(element);

    // Handle the state of the Next button based on disableButtons
    const nextButton = document.getElementById('tourNextButton');
    const skipButton = document.getElementById('tourSkipButton');

    if (step.skipOnly) {
      if (nextButton) nextButton.style.display = 'none';
    } else {
      if (nextButton) nextButton.style.display = 'inline-block';
    }

    if (step.endTour) {
      if (nextButton) nextButton.style.display = 'none';
      if (skipButton) skipButton.innerText = 'End Tour';
    } else {
      if (skipButton) skipButton.innerText = 'Skip Tour';
    }

    if (nextButton && skipButton) {
      if (step.disableButtons) {
        nextButton.disabled = true;
        nextButton.classList.add('disabled'); // Optional: Add a CSS class for styling
        skipButton.disabled = true;
        skipButton.classList.add('disabled'); // Optional: Add a CSS class for styling
      } else {
        nextButton.disabled = false;
        nextButton.classList.remove('disabled'); // Optional: Remove the CSS class
        skipButton.disabled = false;
        skipButton.classList.remove('disabled'); // Optional: Remove the CSS class
      }
    }

    // Update tooltip content
    const tooltip = document.getElementById('highlightTooltip');
    if (tooltip) {
      document.getElementById('tourTitle').textContent = step.title;
      document.getElementById('tourDescription').innerHTML = step.description; // Changed to innerHTML
      this.positionTooltip(rect, step.position || 'above');
    }

    // If the step has a callback, bind it to the link
    if (step.callback && step.linkSelector && tooltip) {
      const link = tooltip.querySelector(step.linkSelector);
      if (link) {
        // Remove existing listeners to prevent multiple bindings
        if (step.boundCallback) {
          link.removeEventListener('click', step.boundCallback);
        }
        step.boundCallback = (event) => {
          event.preventDefault(); // Prevent default link behavior
          step.callback();
        };
        link.addEventListener('click', step.boundCallback);
      } else {
        console.warn(
          `Link with selector '${step.linkSelector}' not found in tourDescription.`
        );
      }
    }

    // **Automatic Scroll to Active Element**
    this.scrollToElementIfNeeded(element);
  }

  scrollToElementIfNeeded(el) {
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.visualViewport
      ? window.visualViewport.height
      : window.innerHeight;
    const viewportWidth = window.visualViewport
      ? window.visualViewport.width
      : window.innerWidth;

    const isVisible =
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= viewportHeight &&
      rect.right <= viewportWidth;

    if (!isVisible) {
      el.scrollIntoView({ behavior: 'auto', block: 'center' });
    }
  }

  setStepElement(stepIndex, elementId) {
    const step = this.steps[stepIndex];
    if (!step) {
      console.warn(`FeatureTour: Step index ${stepIndex} does not exist.`);
      return false;
    }

    if (!elementId) {
      console.warn('FeatureTour: Provided elementId is invalid.');
      return false;
    }

    const element = document.getElementById(elementId);

    if (!element) {
      console.warn(
        `FeatureTour: Element with id '${elementId}' not found. Unable to update step.`
      );
      return false;
    }

    step.elementId = elementId;

    if (stepIndex === this.currentStep && this.isVisible) {
      this.showStep(stepIndex);
    }

    return true;
  }

  /**
   * Finds the element by ID.
   * @param {string} id - The ID of the element to find.
   * @returns {HTMLElement|null} - The found element or null.
   */
  findElement(id) {
    const el = document.getElementById(id);
    if (el) {
      return el;
    }
    return null;
  }

  showFourOverlays(el) {
    const highlightBox = document.getElementById('highlightBox');
    if (!highlightBox) {
      console.warn('Highlight box element not found.');
      return el.getBoundingClientRect();
    }

    const rect = el.getBoundingClientRect();

    // Use visualViewport if available
    const viewportWidth = window.visualViewport
      ? window.visualViewport.width
      : window.innerWidth;
    const viewportHeight = window.visualViewport
      ? window.visualViewport.height
      : window.innerHeight;

    const topOverlay = document.querySelector('.overlay-top');
    const leftOverlay = document.querySelector('.overlay-left');
    const rightOverlay = document.querySelector('.overlay-right');
    const bottomOverlay = document.querySelector('.overlay-bottom');

    if (!topOverlay || !leftOverlay || !rightOverlay || !bottomOverlay) {
      console.warn('One or more overlay elements not found.');
      return rect;
    }

    // Ensure overlays are visible
    topOverlay.style.display = 'block';
    leftOverlay.style.display = 'block';
    rightOverlay.style.display = 'block';
    bottomOverlay.style.display = 'block';

    // Position the top overlay to cover from top of screen down to highlight top
    topOverlay.style.top = '0px';
    topOverlay.style.left = '0px';
    topOverlay.style.width = `${viewportWidth}px`;
    topOverlay.style.height = `${rect.top}px`;

    // Left overlay covers area to the left of the highlight rectangle
    leftOverlay.style.top = `${rect.top}px`;
    leftOverlay.style.left = '0px';
    leftOverlay.style.width = `${rect.left}px`;
    leftOverlay.style.height = `${rect.height}px`;

    // Right overlay covers area to the right of the highlight rectangle
    const rightOverlayLeft = rect.left + rect.width;
    rightOverlay.style.top = `${rect.top}px`;
    rightOverlay.style.left = `${rightOverlayLeft}px`;
    rightOverlay.style.width = `${viewportWidth - rightOverlayLeft}px`;
    rightOverlay.style.height = `${rect.height}px`;

    // Bottom overlay covers everything below the highlight rectangle
    bottomOverlay.style.top = `${rect.top + rect.height}px`;
    bottomOverlay.style.left = '0px';
    bottomOverlay.style.width = `${viewportWidth}px`;
    bottomOverlay.style.height = `${viewportHeight - (rect.top + rect.height)}px`;

    // Position highlight box around the element
    highlightBox.style.display = 'block';
    highlightBox.style.top = `${rect.top}px`;
    highlightBox.style.left = `${rect.left}px`;
    highlightBox.style.width = `${rect.width - 2}px`;
    highlightBox.style.height = `${rect.height - 2}px`;

    return rect;
  }

  positionTooltip(rect, position = 'above') {
    const tooltip = document.getElementById('highlightTooltip');
    if (!tooltip) {
      console.warn('Tooltip element not found.');
      return;
    }
    tooltip.style.display = 'block';

    // Calculate tooltip positioning
    const tooltipRect = tooltip.getBoundingClientRect();

    let top, left;
    if (position === 'above') {
      top = rect.top - tooltipRect.height - 10; // 10px above
      left = rect.left + rect.width / 2 - tooltipRect.width / 2;
    } else if (position === 'below') {
      top = rect.bottom + 10;
      left = rect.left + rect.width / 2 - tooltipRect.width / 2;
    } else if (position === 'left') {
      top = rect.top + rect.height / 2 - tooltipRect.height / 2;
      left = rect.left - tooltipRect.width - 10;
    } else {
      // default to right
      top = rect.top + rect.height / 2 - tooltipRect.height / 2;
      left = rect.right + 10;
    }

    // Ensure tooltip is visible within viewport (simple clamp)
    const viewportHeight = window.visualViewport
      ? window.visualViewport.height
      : window.innerHeight;
    const viewportWidth = window.visualViewport
      ? window.visualViewport.width
      : window.innerWidth;

    top = Math.max(10, Math.min(top, viewportHeight - tooltipRect.height - 10));
    left = Math.max(10, Math.min(left, viewportWidth - tooltipRect.width - 10));

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
  }

  handleNext() {
    this.nextStep();
  }

  handleSkip() {
    this.endTour();
    this.disable();
    stateManager.updateState({ tourSkipped: true });
  }

  /**
   * Handles clicks on the overlay. Optionally, you can decide to close the tour or ignore clicks.
   * @param {Event} event
   */
  handleOverlayClick(event) {
    // Prevent clicks from propagating to underlying elements
    event.stopPropagation();
    // Optionally, you can decide to end the tour when the overlay is clicked
    // this.endTour();
  }

  nextStep() {
    const { tourSkipped } = stateManager.getState();

    if (!this.enabled || tourSkipped) {
      return;
    }

    this.currentStep++;
    if (this.currentStep < this.steps.length) {
      this.showStep(this.currentStep);
    } else {
      this.endTour();
    }
  }

  enable() {
    this.enabled = true;
  }

  disable() {
    this.enabled = false;
  }

  /**
   * Shows the tour UI elements without altering the tour's state.
   */
  show() {
    if (!this.enabled) return;

    if (this.isVisible) return; // Prevent redundant operations
    this.isVisible = true;

    const overlay = document.getElementById('tourOverlay');
    const highlightBox = document.getElementById('highlightBox');
    const tooltip = document.getElementById('highlightTooltip');

    if (overlay) {
      overlay.style.display = 'block';
    }
    if (highlightBox) {
      highlightBox.style.display = 'block';
    }
    if (tooltip) {
      tooltip.style.display = 'block';
    }

    console.log('FeatureTour: Tour UI elements are now visible.');
  }

  /**
   * Hides the tour UI elements without ending the tour.
   */
  hide() {
    if (!this.enabled) return;

    if (!this.isVisible) return; // Prevent redundant operations
    this.isVisible = false;

    const overlay = document.getElementById('tourOverlay');
    const highlightBox = document.getElementById('highlightBox');
    const tooltip = document.getElementById('highlightTooltip');

    if (overlay) {
      overlay.style.display = 'none';
    }
    if (highlightBox) {
      highlightBox.style.display = 'none';
    }
    if (tooltip) {
      tooltip.style.display = 'none';
    }

    console.log('FeatureTour: Tour UI elements are now hidden.');
  }

  endTour() {
    if (!this.enabled) {
      return;
    }

    const tooltip = document.getElementById('highlightTooltip');
    const box = document.getElementById('highlightBox');

    if (tooltip) tooltip.style.display = 'none';
    if (box) box.style.display = 'none';

    const overlays = document.querySelectorAll('.overlay');
    overlays.forEach((ov) => {
      ov.style.width = '0';
      ov.style.height = '0';
      ov.style.display = 'none'; // Hide overlays
    });

    // Unbind event listeners to prevent memory leaks
    const nextButton = document.getElementById('tourNextButton');
    const skipButton = document.getElementById('tourSkipButton');

    if (nextButton) {
      nextButton.removeEventListener('click', this.handleNext);
    }

    if (skipButton) {
      skipButton.removeEventListener('click', this.handleSkip);
    }

    const overlay = document.getElementById('overlay');
    if (overlay) {
      overlay.removeEventListener('click', this.handleOverlayClick);
    }

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.resizeHandler);
    } else {
      window.removeEventListener('resize', this.resizeHandler);
    }

    this.scrollableElement.removeEventListener('scroll', this.scrollHandler);

    // Remove input focus and blur listeners
    this.inputs.forEach((input) => {
      input.removeEventListener('focus', this.handleInputFocus);
      input.removeEventListener('blur', this.handleInputBlur);
    });

    // Remove link event listeners if any
    this.steps.forEach((step) => {
      if (step.boundCallback && step.linkSelector) {
        const tooltip = document.getElementById('highlightTooltip');
        if (tooltip) {
          const link = tooltip.querySelector(step.linkSelector);
          if (link) {
            link.removeEventListener('click', step.boundCallback);
          }
        }
      }
    });

    // Reset current step and keyboard state
    this.currentStep = 0;
    this.keyboardVisible = false;
    this.focusCounter = 0;

    // Cancel any pending animation frames
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    // Reset visibility state
    this.isVisible = false;

    console.log('FeatureTour: Tour has ended.');
  }
}

export { FeatureTour };
